import Select from "react-select";
import moment from "moment";
import { Checkbox } from "material-ui";

import Label from "./label.jsx";

const styles = {
  selectContainer: {
    margin: -5
  },
  selectBlock: {
    display: "inline-block",
    verticalAlign: "top",
    width: "calc(25% - 10px)",
    minWidth: 175,
    padding: 5
  }
};

function _makeRecipientDescription(
  query,
  eventBucketId,
  { PassBucketStore, EventStore, CouponStore, EventBucketStore, TagStore }
) {
  let currentPassBuckets = (query.pass_bucket || [])
      .map(b => PassBucketStore.getPassBucketById(b))
      .filter(o => !!o),
    currentEvents = (query.event || [])
      .map(e => EventStore.getEventById(e))
      .filter(o => !!o),
    currentCoupons = (query.coupon || [])
      .map(c => CouponStore.getCouponById(c))
      .filter(o => !!o),
    currentTags = (query.tag || [])
      .map(e => TagStore.getTagById(e))
      .filter(o => !!o);

  let description = "";

  if (query.all_attendees) {
    return `All ${
      (EventBucketStore.getEventBucketById(eventBucketId) || { name: "" }).name
    } attendees`;
  } else if (query.all_standalone_tickets_only) {
    return `All ${
      (EventBucketStore.getEventBucketById(eventBucketId) || { name: "" }).name
    } standalone ticketholders (excluding passholders)`;
  }

  function _groupMembers(group, joiner = "and") {
    let labels = group.map(o => `"${o.name}"`);

    if (labels.length === 1) {
      return labels[0];
    }

    let result = labels.slice(0, -1).join(", ");
    result += `${labels.length > 2 ? "," : ""} ${joiner} ${
      labels[labels.length - 1]
    }`;

    return result;
  }

  if (currentPassBuckets.length) {
    description += `${_groupMembers(currentPassBuckets)} passholders`;
  } else if (currentEvents.length || currentTags.length) {
    description += `Attendees to`;
  } else if (currentCoupons.length) {
    description += `Customers`;
  }

  if (
    currentPassBuckets.length &&
    (currentEvents.length || currentTags.length)
  ) {
    description += ` who attended`;
  }

  if (currentEvents.length) {
    description += ` ${_groupMembers(currentEvents, "or")}`;
  }

  if (currentTags.length) {
    if (currentEvents.length) {
      description += ` or`;
    }
    description += ` events tagged`;
    description += ` ${_groupMembers(currentTags, "or")}`;
  }

  if (currentCoupons.length) {
    if (!currentEvents.length || !currentPassBuckets.length) {
      description += ` who`;
    } else {
      description += ` and`;
    }

    description += ` used the ${_groupMembers(currentCoupons, "or")} discount`;
  }

  return description;
}

const ListTargetBuilder = ({
  label,
  query,
  eventBucketId,
  allLabel,
  standaloneLabel,
  stores,
  onChange
}) => {
  let buckets = stores.PassBucketStore.getPassBucketsForEventBucket(
      eventBucketId
    ),
    events = stores.EventStore.getEventsForEventBucket(eventBucketId),
    coupons = stores.CouponStore.getCouponsForEventBucket(eventBucketId),
    tags = stores.TagStore.getTagsForEventBucket(eventBucketId);

  let passBucketMenuItems = buckets
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((b, i) => {
        return { value: b.id, label: b.name };
      }),
    eventMenuItems = events
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((e, i) => {
        return {
          value: e.id,
          label: `${e.name} (${moment(e.start_time).format("llll")})`
        };
      }),
    couponMenuItems = coupons
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c, i) => {
        return { value: c.id, label: c.name };
      }),
    tagMenuItems = tags
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((t, i) => {
        return { value: t.id, label: t.name };
      });

  return (
    <div>
      <Label>{label}</Label>
      <div style={{ marginBottom: 5 }}>
        Selected audience:{" "}
        {_makeRecipientDescription(query, eventBucketId, stores) || (
          <span>
            None yet. <em>Combine selectors for powerful targeting.</em>
          </span>
        )}
      </div>
      {query.all_attendees || query.all_standalone_tickets_only ? null : (
        <div style={styles.selectContainer}>
          <div style={styles.selectBlock}>
            <Select
              autosize={false}
              multi={true}
              placeholder="No pass buckets selected"
              value={query.pass_bucket}
              options={passBucketMenuItems}
              onChange={value => {
                onChange("query.pass_bucket", value && value.map(o => o.value));
              }}
            />
          </div>
          <div style={styles.selectBlock}>
            <Select
              autosize={false}
              multi={true}
              placeholder="No events selected"
              value={query.event}
              options={eventMenuItems}
              onChange={value => {
                onChange("query.event", value && value.map(o => o.value));
              }}
            />
          </div>
          <div style={styles.selectBlock}>
            <Select
              autosize={false}
              multi={true}
              placeholder="No tags selected"
              value={query.tag}
              options={tagMenuItems}
              onChange={value => {
                onChange("query.tag", value && value.map(o => o.value));
              }}
            />
          </div>
          <div style={styles.selectBlock}>
            <Select
              autosize={false}
              multi={true}
              placeholder="No discounts selected"
              value={query.coupon}
              options={couponMenuItems}
              onChange={value => {
                onChange("query.coupon", value && value.map(o => o.value));
              }}
            />
          </div>
        </div>
      )}
      <div style={{ marginTop: "0.5em" }}>
        {!query.all_standalone_tickets_only ? (
          <Checkbox
            label={allLabel || "Send to all attendees (pass & ticket holders)"}
            checked={query.all_attendees}
            onCheck={() =>
              onChange("query", { all_attendees: !query.all_attendees })
            }
          />
        ) : null}
        {!query.all_attendees ? (
          <Checkbox
            label={
              standaloneLabel ||
              "Send to all standalone ticketholders (excluding passholders)"
            }
            checked={query.all_standalone_tickets_only}
            onCheck={() =>
              onChange("query", {
                all_attendees: false,
                all_standalone_tickets_only: !query.all_standalone_tickets_only
              })
            }
          />
        ) : null}
      </div>
    </div>
  );
};

ListTargetBuilder._makeRecipientDescription = _makeRecipientDescription;

export default ListTargetBuilder;



// WEBPACK FOOTER //
// ./src/app/components/partials/list-target-builder.jsx